'use client';

/**
 * Orchestrates file-watcher-triggered (externally initiated) refreshes.
 *
 * This module intentionally sits between `fileWatcherHandler.ts` and `postBuildRefresh.ts`
 * to add play-mode awareness without polluting the lower-level refresh primitive.
 *
 * Listener teardown: `project.teardownFileSystemChangeListener()` is called from
 * `useSafeNavigation.ts` whenever the user leaves the /editor route, so any stamp event
 * that fires while the editor is closed will never reach this function.
 */

import { toast } from 'sonner';

import {
  isBuildStampAlreadyApplied,
  readCurrentBuildStampBuiltAt,
} from './buildStampState';
import { refreshAfterBuild } from './postBuildRefresh';

import { globalStore } from '@/app/globalStore';
import { editorScene, editorStore } from '@/editor/store/editorStore';
import { IS_DEV } from '@/lib/utils';

const OVERLAY_KEY = 'autoRestartPlayMode';
const DEBUG_LOG = {
  queue: false && IS_DEV,
  skip: IS_DEV,
} as const;

/** True while an external-build-triggered refresh is already running. */
let externalRefreshInProgress = false;
/** Single merged external refresh request captured while execution is blocked. */
let queuedExternalRefresh = false;

/**
 * Handle a file-watcher-detected build-stamp change.
 *
 * Guards:
 * - Project must be loaded (editor open with a project).
 * - Our own in-app build must not be running (it handles its own `refreshAfterBuild`).
 * - Only one external refresh at a time.
 *
 * If play mode is active the editor performs a clean exit → full refresh → re-entry
 * (auto-restart) so the user's latest compiled code is active without manual intervention.
 */
export async function refreshAfterExternalBuild(): Promise<void> {
  if (externalRefreshInProgress || editorStore.isBuildingProject) {
    queuedExternalRefresh = true;
    if (DEBUG_LOG.queue) {
      console.debug('[ExternalRefresh] Queued while refresh/build is already active');
    }
    return;
  }

  if (!editorStore.project.isProjectOpened) {
    return;
  }

  const builtAt = await readCurrentBuildStampBuiltAt();
  if (isBuildStampAlreadyApplied(builtAt)) {
    queuedExternalRefresh = false;
    if (DEBUG_LOG.skip) {
      console.debug('[ExternalRefresh] Skipping refresh — build stamp already applied');
    }
    return;
  }
  if (builtAt == null && editorScene.isPlayModeStarting) {
    queuedExternalRefresh = true;
    if (DEBUG_LOG.queue) {
      console.debug('[ExternalRefresh] Deferred — stamp unreadable while Play Mode is starting');
    }
    return;
  }

  externalRefreshInProgress = true;
  try {
    await runExternalRefreshCycle();
  } catch (error) {
    console.error('[ExternalRefresh] Failed to apply external build refresh:', error);
    toast.error('Failed to refresh after script changes');
  } finally {
    externalRefreshInProgress = false;
  }

  await drainQueuedExternalRefresh();
}

/**
 * Attempts to run a merged queued external refresh when runtime guards allow it.
 * Returns true if the queued refresh was consumed.
 */
export async function drainQueuedExternalRefresh(): Promise<boolean> {
  if (!queuedExternalRefresh) {
    return false;
  }
  if (externalRefreshInProgress || editorStore.isBuildingProject) {
    return false;
  }
  if (!editorStore.project.isProjectOpened) {
    queuedExternalRefresh = false;
    return false;
  }

  queuedExternalRefresh = false;
  if (DEBUG_LOG.queue) {
    console.debug('[ExternalRefresh] Draining queued external refresh');
  }
  await refreshAfterExternalBuild();
  return true;
}

/** Queue one merged external refresh request and attempt a drain immediately. */
export function queueExternalRefreshAndDrainSoon(): void {
  queuedExternalRefresh = true;
  if (DEBUG_LOG.queue) {
    console.debug('[ExternalRefresh] Queued external refresh intent');
  }
  if (!externalRefreshInProgress && !editorStore.isBuildingProject) {
    void drainQueuedExternalRefresh().catch(error => {
      console.error('[ExternalRefresh] Failed to drain queued external refresh:', error);
      toast.error('Failed to apply queued script refresh');
    });
  }
}

async function runExternalRefreshCycle(): Promise<void> {
  const wasInPlayMode = editorScene.isPlayMode || editorScene.isPlayModeStarting;

  if (wasInPlayMode) {
    await restartPlayModeAroundRefresh();
  } else {
    await refreshAfterBuild();
  }
}

/**
 * Exit play mode, apply the fresh build, then re-enter play mode.
 * A persistent loading overlay covers the transition so the user sees a single
 * coherent "Restarting Play Mode..." message for the full operation.
 */
async function restartPlayModeAroundRefresh(): Promise<void> {
  // Lazy import to avoid a circular dependency:
  // editorActions -> refreshAfterBuild -> (this module) -> editorActions
  const { setPlayModeActive } = await import('@/editor/core/actions/editorActions');

  console.log('[ExternalRefresh] 🔄 External build detected while in play mode — auto-restarting...');
  toast.info('Scripts changed — restarting Play Mode...');

  globalStore.showLoadingOverlay(OVERLAY_KEY, {
    message: 'Restarting Play Mode...',
    pinned: true,
    useDimmedBackdrop: true,
  });

  try {
    await setPlayModeActive(false);

    // Full refresh is now safe: the editor world is back in edit mode.
    await refreshAfterBuild();

    await setPlayModeActive(true);
  } catch (error) {
    console.error('[ExternalRefresh] Failed to auto-restart play mode after external build:', error);
    toast.error('Auto-restart failed — enter Play Mode manually after the build');
  } finally {
    globalStore.hideLoadingOverlay(OVERLAY_KEY);
  }
}
